import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@repo/ui/components/ui/dialog'
import { Database, Link2, Shield, User } from 'lucide-react'
import { useEffect, useState } from 'react'
import {
  AppWorkspacePane,
  AppWorkspacePanel,
  AppWorkspaceSidebarDetailSplit,
} from '@/components/app/workspace-shell'
import { SettingsSidebarItem } from './settings-sidebar-item'
import { AccountsTab } from './tabs/accounts-tab'
import { DataTab } from './tabs/data-tab'
import { GeneralTab } from './tabs/general-tab'
import { SecurityTab } from './tabs/security-tab'

interface SettingsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultTab?: SettingsTabValue
}

const SIDEBAR_ITEMS = [
  { value: 'general', label: 'Account', icon: User },
  { value: 'security', label: 'Security', icon: Shield },
  { value: 'accounts', label: 'Sign-in Providers', icon: Link2 },
  { value: 'data', label: 'Data Controls', icon: Database },
] as const

export type SettingsTabValue = (typeof SIDEBAR_ITEMS)[number]['value']

interface SettingsWorkspaceProps {
  defaultTab?: SettingsTabValue
  className?: string
  framed?: boolean
  onSignedOut?: () => void
  onAccountDeleted?: () => void
}

export function SettingsWorkspace({
  defaultTab = 'general',
  className,
  framed = true,
  onSignedOut,
  onAccountDeleted,
}: SettingsWorkspaceProps) {
  const [activeTab, setActiveTab] = useState<SettingsTabValue>(defaultTab)

  useEffect(() => {
    setActiveTab(defaultTab)
  }, [defaultTab])

  const content = (
    <AppWorkspaceSidebarDetailSplit className={className}>
      <AppWorkspacePane
        as="nav"
        surface="region"
        className="bg-muted/20 p-3"
        aria-label="Settings navigation"
      >
        <div className="flex gap-1.5 overflow-x-auto lg:flex-col lg:overflow-visible">
          {SIDEBAR_ITEMS.map((item) => (
            <SettingsSidebarItem
              key={item.value}
              icon={item.icon}
              label={item.label}
              active={activeTab === item.value}
              onClick={() => setActiveTab(item.value)}
            />
          ))}
        </div>
      </AppWorkspacePane>

      <AppWorkspacePane scroll className="px-4 py-4 sm:px-6 sm:py-6">
        {activeTab === 'general' && <GeneralTab onSignedOut={onSignedOut} />}
        {activeTab === 'security' && <SecurityTab />}
        {activeTab === 'accounts' && <AccountsTab />}
        {activeTab === 'data' && <DataTab onAccountDeleted={onAccountDeleted} />}
      </AppWorkspacePane>
    </AppWorkspaceSidebarDetailSplit>
  )

  if (!framed) return content

  return <AppWorkspacePanel>{content}</AppWorkspacePanel>
}

export function SettingsDialog({
  open,
  onOpenChange,
  defaultTab = 'general',
}: SettingsDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[92vh] max-h-[660px] max-w-[calc(100%-1.5rem)] flex-col gap-0 overflow-hidden rounded-[4px] border border-border/70 bg-background p-0 shadow-lg sm:max-w-3xl md:max-w-5xl">
        <DialogHeader className="sr-only">
          <DialogTitle className="text-2xl leading-tight font-semibold tracking-tight">
            Settings
          </DialogTitle>
        </DialogHeader>
        <SettingsWorkspace
          defaultTab={defaultTab}
          framed={false}
          className="rounded-none border-0 shadow-none"
          onSignedOut={() => onOpenChange(false)}
          onAccountDeleted={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  )
}
